import React, { useState, useEffect } from 'react';
import { BookIndex, ChapterInfo } from '../types/book';
import { BookApiService } from '../services/bookApi';

interface ChapterListProps {
  book: BookIndex;
  onChapterSelect: (book: BookIndex, chapter: number) => void;
  onBack: () => void;
}

const ChapterList: React.FC<ChapterListProps> = ({ book, onChapterSelect, onBack }) => {
  const [chapterList, setChapterList] = useState<ChapterInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // API로 챕터 리스트 가져오기
  useEffect(() => {
    const fetchChapters = async () => {
      try {
        setLoading(true);
        const chapters = await BookApiService.getBookChapters(book.bookNo);
        setChapterList(chapters);
      } catch (error) {
        console.error('챕터 리스트 조회 중 오류:', error);
        setChapterList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [book.bookNo]);

  // API 응답이 있으면 사용, 없으면 book.chapters 사용
  const hasApiChapters = chapterList.length > 0;
  const chapters = hasApiChapters 
    ? chapterList.map(c => c.chapter)
    : Array.from({ length: book.chapters }, (_, i) => i + 1);

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // 모바일에서 렌더링 문제 해결을 위한 강제 리렌더링
  useEffect(() => {
    if (isMobile) {
      // 컴포넌트 마운트 후 강제 리렌더링
      const timer = setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  return (
    <div style={{ padding: '20px' }}>
      {/* 이전 버튼 - 헤더 바로 아래 왼쪽 정렬 */}
      <div style={{ marginBottom: '10px', textAlign: 'left' }}>
        <button 
          onClick={onBack}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← 이전
        </button>
      </div>
      
      {/* Book 제목 - 별도 줄 */}
      <div style={{ maxWidth: '800px', margin: '0 auto', marginBottom: '20px' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ 
            margin: 0, 
            color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
            fontSize: isMobile ? '1.0rem' : undefined,
            fontWeight: 'bold'
          }}>
            {book.name}
          </h2>
          {(() => {
            const hasNameEN = book.nameEN && book.nameEN !== 'null' && book.nameEN.trim() !== '';
            const hasNameZH = book.nameZH && book.nameZH !== 'null' && book.nameZH.trim() !== '';
            const displayName = hasNameEN ? book.nameEN : (hasNameZH ? book.nameZH : '');
            return displayName && (
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: isMobile ? '0.8rem' : '0.9rem', 
                color: '#6c757d',
                fontWeight: 'normal'
              }}>
                {displayName}
              </p>
            );
          })()}
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>챕터를 불러오는 중...</p>
        </div>
      ) : hasApiChapters ? (
        // API 응답이 있는 경우: chapter와 title을 표시하고 한 열에 2개씩
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: isMobile ? '10px' : '15px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {chapterList.map((chapterInfo) => (
            <div 
              key={chapterInfo.chapter}
              onClick={() => onChapterSelect(book, chapterInfo.chapter)}
              style={{ 
                border: '1px solid #ddd', 
                borderRadius: isMobile ? '6px' : '8px', 
                padding: isMobile ? '12px 8px' : '15px',
                backgroundColor: book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: isMobile ? '12px' : '16px',
                fontWeight: 'bold',
                color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#e9ecef' : '#d1ecf1';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: isMobile ? '14px' : '18px' }}>
                #{chapterInfo.chapter}
              </div>
              {chapterInfo.title && (
                <div style={{ 
                  fontSize: isMobile ? '15px' : '18px', 
                  fontWeight: 'normal',
                  color: '#666',
                  marginTop: '4px'
                }}>
                  {chapterInfo.title}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // API 응답이 없는 경우: 현재대로 표시
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile 
            ? 'repeat(auto-fill, minmax(60px, 1fr))' 
            : 'repeat(auto-fill, minmax(80px, 1fr))', 
          gap: isMobile ? '6px' : '10px' 
        }}>
          {chapters.map((chapter) => (
            <div 
              key={chapter}
              onClick={() => onChapterSelect(book, chapter)}
              style={{ 
                border: '1px solid #ddd', 
                borderRadius: isMobile ? '6px' : '8px', 
                padding: isMobile ? '8px 4px' : '15px',
                backgroundColor: book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: isMobile ? '12px' : '16px',
                fontWeight: 'bold',
                color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
                minHeight: isMobile ? '40px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#e9ecef' : '#d1ecf1';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              #{chapter}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChapterList;
